import { billingClient } from '../../billing-api-integration/client';
import { GraphQLQuery } from '@aws-amplify/api';
import { notifications } from '@mantine/notifications';
import { listEnabledBillingProviders, getBillingProvider } from './queries';
import { notifyError, showSuccessNotification } from '../../../../components/ServiceNotifications';
import { isCustomerNotLinkedError, CUSTOMER_NOT_LINKED_MESSAGE } from '../../billing-api-integration/errors';
import { ListEnabledBillingProvidersResponse, GetBillingProviderResponse, BillingProvider } from '../types';

// FNP-724: a CUSTOMER_NOT_LINKED error (typed by FNP-718 on the *ForCurrentCustomer ops) means
// the account->customer link is still settling — show a friendly "still setting up" info toast
// instead of the raw red error, and re-throw so the caller's mutation still resolves to error.
const notifyBillingError = (e: unknown) => {
    if (isCustomerNotLinkedError(e)) {
        notifications.show({
            title: 'Billing profile is still setting up',
            message: CUSTOMER_NOT_LINKED_MESSAGE,
            color: 'blue',
        });
        return;
    }
    notifyError(e);
};

type UpdateBillingProviderInput = Partial<Pick<BillingProvider, 'name' | 'key' | 'status' | 'description'>>;
type UpdateBillingProviderResponse = { updateBillingProvider: BillingProvider };

type ProviderMappingResult = {
    id: string;
    entityType: string;
    localEntityId: string;
    billingProviderId: string;
    providerEntityId: string;
    syncStatus: string;
};

type EnableBillingProviderResponse = {
    enableBillingProviderForCurrentCustomer: ProviderMappingResult;
};

type DisableBillingProviderResponse = {
    disableBillingProviderForCurrentCustomer: { id: string };
};

export const fetchEnabledBillingProviders = async (): Promise<BillingProvider[]> => {
    try {
        const response = await billingClient.graphql<GraphQLQuery<ListEnabledBillingProvidersResponse>>({
            query: listEnabledBillingProviders
        });
        return response.data?.listEnabledBillingProviders?.items || [];
    } catch (e) {
        console.trace(e);
        notifyError(e);
        return [];
    }
};

export const fetchBillingProvider = async (id: string): Promise<BillingProvider | undefined> => {
    try {
        const response = await billingClient.graphql<GraphQLQuery<GetBillingProviderResponse>>({
            query: getBillingProvider,
            variables: { id }
        });
        return response.data?.getBillingProvider;
    } catch (e) {
        console.trace(e);
        notifyError(e);
    }
};

export const updateBillingProvider = async (id: string, input: UpdateBillingProviderInput) => {
    try {
        const updateMutation = `
            mutation updateBillingProvider($input: UpdateBillingProviderInput!) {
                updateBillingProvider(input: $input) {
                    id
                    name
                    key
                    status
                }
            }
        `;
        const response = await billingClient.graphql<GraphQLQuery<UpdateBillingProviderResponse>>({
            query: updateMutation,
            variables: { input: { id, ...input } }
        });
        showSuccessNotification('Provider updated successfully');
        return response.data?.updateBillingProvider;
    } catch (e) {
        console.trace(e);
        notifyError(e);
        throw e;
    }
};

export const enableBillingProviderForCurrentCustomer = async (billingProviderId: string) => {
    try {
        const mutation = `
            mutation enableBillingProviderForCurrentCustomer($billingProviderId: ID!) {
                enableBillingProviderForCurrentCustomer(billingProviderId: $billingProviderId) {
                    id
                    entityType
                    localEntityId
                    billingProviderId
                    providerEntityId
                    syncStatus
                }
            }
        `;
        const response = await billingClient.graphql<GraphQLQuery<EnableBillingProviderResponse>>({
            query: mutation,
            variables: { billingProviderId }
        });
        showSuccessNotification('Provider enabled successfully');
        return response.data?.enableBillingProviderForCurrentCustomer;
    } catch (e) {
        console.trace(e);
        notifyBillingError(e);
        throw e;
    }
};

export const disableBillingProviderForCurrentCustomer = async (billingProviderId: string) => {
    try {
        const mutation = `
            mutation disableBillingProviderForCurrentCustomer($billingProviderId: ID!) {
                disableBillingProviderForCurrentCustomer(billingProviderId: $billingProviderId) {
                    id
                }
            }
        `;
        const response = await billingClient.graphql<GraphQLQuery<DisableBillingProviderResponse>>({
            query: mutation,
            variables: { billingProviderId }
        });
        showSuccessNotification('Provider disabled successfully');
        return response.data?.disableBillingProviderForCurrentCustomer;
    } catch (e) {
        console.trace(e);
        notifyBillingError(e);
        throw e;
    }
};
